/**
 * O CSS do documento — um só para tela, e-mail (HTML autocontido) e PDF.
 * Sem Tailwind: tokens como variáveis CSS, para o mesmo arquivo valer em qualquer host.
 * Fonte da verdade dos valores: docs/styleguide/02-tokens.md.
 */

export interface CssOptions {
  /** Texto do rodapé de impressão à esquerda (protocolo · hash). */
  footerLeft?: string
  /** Texto do rodapé de impressão à direita antes do número da página. */
  footerRight?: string
}

function cssString(s: string) {
  return `"${s.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`
}

export function documentCss(options: CssOptions = {}): string {
  const left = options.footerLeft ? cssString(options.footerLeft) : '""'
  const right = options.footerRight ? cssString(`${options.footerRight} · `) : '""'
  return `
.wx-legal{
  --wx-ink:#1a1a1a;--wx-pampas:#f0eee9;--wx-surface:#ffffff;--wx-plum:#3b3045;--wx-lilac:#a692ba;
  --wx-alto:#d2d1d1;--wx-secondary:#565060;--wx-label:#5b5560;--wx-hair:rgba(26,26,26,.12);
  --wx-hair-strong:rgba(26,26,26,.16);--wx-hair-heavy:rgba(26,26,26,.22);
  --wx-display:'Lexend Variable',Lexend,sans-serif;--wx-body:Lato,sans-serif;
  --wx-scale:1;--wx-max:680px;
  box-sizing:border-box;max-width:var(--wx-max);margin:0;color:var(--wx-ink);
  font:300 calc(15px*var(--wx-scale))/1.75 var(--wx-body);
  -webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility;
  overflow-wrap:anywhere;hyphens:manual;
}
.wx-legal *,.wx-legal *::before,.wx-legal *::after{box-sizing:inherit}
.wx-legal--large{--wx-scale:1.2}
.wx-legal--print{--wx-max:none;background:var(--wx-surface);padding:22mm 18mm 24mm;min-height:297mm;position:relative;z-index:0}

/* Cabeçalho do documento */
.wx-header{padding-bottom:20px;border-bottom:1px solid var(--wx-hair-strong);margin-bottom:28px}
/* Logo da clínica (P-DOC-06): cabe numa caixa de 40 × 20 mm na impressão (160 × 80 px na tela), proporção mantida. */
.wx-header-logo{margin:0 0 20px}
.wx-header-logo img{display:block;width:auto;height:auto;max-width:160px;max-height:80px}
.wx-legal--print .wx-header-logo img{max-width:40mm;max-height:20mm}
/* Marca d'água: 100 mm, centralizada, a 12 %, atrás do texto. Na impressão se repete em toda página. */
.wx-watermark{position:absolute;top:148mm;left:50%;width:100mm;transform:translate(-50%,-50%);opacity:.12;z-index:-1;pointer-events:none}
.wx-watermark img{display:block;width:100%;height:auto;max-height:100mm;object-fit:contain}
.wx-header-type{font:300 11px/1 var(--wx-body);letter-spacing:.18em;text-transform:uppercase;color:var(--wx-label)}
.wx-header-title{font:300 calc(26px*var(--wx-scale))/1.2 var(--wx-display);color:var(--wx-ink);margin:12px 0 0;text-wrap:pretty}
.wx-header-meta{font:300 12px/1.6 var(--wx-body);color:var(--wx-secondary);margin-top:10px}

/* Títulos: Lexend estrutura */
.wx-legal .wx-h{font-family:var(--wx-display);color:var(--wx-ink);text-wrap:pretty;break-after:avoid}
.wx-legal .wx-h2{font-size:calc(19px*var(--wx-scale));line-height:1.3;font-weight:400;margin:40px 0 12px;padding-top:20px;border-top:1px solid var(--wx-hair)}
.wx-legal .wx-h3{font-size:calc(15px*var(--wx-scale));line-height:1.4;font-weight:400;margin:24px 0 8px}
.wx-legal .wx-h4{font-size:calc(14px*var(--wx-scale));line-height:1.4;font-weight:400;margin:20px 0 6px}
.wx-legal .wx-h5,.wx-legal .wx-h6{font-size:calc(14px*var(--wx-scale));font-weight:400;margin:20px 0 6px}
.wx-num{display:inline-block;min-width:2.2em;color:var(--wx-label);font-weight:300;font-variant-numeric:tabular-nums}

/* Corpo */
.wx-legal p{margin:0 0 14px}
.wx-legal strong{font-weight:700}
.wx-legal em{font-style:italic}
.wx-legal u,.wx-legal .wx-u{text-decoration:underline;text-decoration-thickness:1px;text-underline-offset:.14em}
.wx-legal del,.wx-legal s{text-decoration:line-through;text-decoration-thickness:1px;color:var(--wx-secondary)}
.wx-legal a{color:var(--wx-plum);text-decoration:none;border-bottom:1px solid rgba(59,48,69,.4)}
.wx-legal ul,.wx-legal ol{margin:0 0 14px;padding-left:1.6em}
.wx-legal li{margin:0 0 6px}
.wx-legal li::marker{color:var(--wx-label)}
.wx-legal ol{list-style:decimal}
.wx-legal blockquote{margin:0 0 16px;padding:0 0 0 16px;border-left:2px solid var(--wx-hair-heavy);color:var(--wx-secondary)}
.wx-legal hr{border:0;border-top:1px solid var(--wx-hair);margin:28px 0}
.wx-legal table{width:100%;border-collapse:collapse;margin:0 0 20px;font-size:calc(13px*var(--wx-scale))}
.wx-legal th{font:300 11px/1 var(--wx-body);letter-spacing:.12em;text-transform:uppercase;color:var(--wx-label);text-align:left;padding:10px 12px;border-bottom:1px solid var(--wx-hair-strong);background:var(--wx-pampas)}
.wx-legal td{padding:10px 12px;border-bottom:1px solid var(--wx-hair);vertical-align:top}
.wx-legal code{font-family:ui-monospace,monospace;font-size:.9em;background:var(--wx-pampas);padding:1px 4px}

/* Blocos de legal design */
.wx-block{margin:0 0 20px;break-inside:avoid}
.wx-block>p:last-child,.wx-block>ul:last-child,.wx-block>ol:last-child{margin-bottom:0}
.wx-block-title{font:300 11px/1 var(--wx-body);letter-spacing:.12em;text-transform:uppercase;color:var(--wx-label);margin:0 0 8px}
.wx-block--summary{padding:0 0 20px;border-bottom:1px solid var(--wx-hair);margin-bottom:24px}
.wx-block--summary p{font:400 calc(17px*var(--wx-scale))/1.5 var(--wx-display);color:var(--wx-ink)}
.wx-block--authorization p{font:400 calc(16px*var(--wx-scale))/1.5 var(--wx-display);color:var(--wx-ink)}
.wx-block--risks{background:var(--wx-pampas);padding:16px 18px}
.wx-block--risks .wx-block-title{font:400 12px/1.3 var(--wx-display);letter-spacing:0;text-transform:none;color:var(--wx-ink)}
.wx-block--attention{background:var(--wx-surface);border-left:2px solid var(--wx-plum);padding:14px 18px}
.wx-block--attention .wx-block-title{color:var(--wx-plum)}
.wx-block--context{border-left:2px solid var(--wx-lilac);padding:12px 18px;color:var(--wx-ink)}
.wx-block--context p{font-size:calc(14px*var(--wx-scale))}
.wx-block--update{border-top:1px solid var(--wx-hair);border-bottom:1px solid var(--wx-hair);padding:12px 0;font-size:calc(13px*var(--wx-scale));color:var(--wx-secondary)}
.wx-block--update .wx-block-title{color:var(--wx-plum)}
.wx-block--parties ul,.wx-parties{list-style:none;padding:0;margin:0}
.wx-parties li{padding:10px 0;border-top:1px solid var(--wx-hair);margin:0}
.wx-parties li:last-child{border-bottom:1px solid var(--wx-hair)}
.wx-parties li strong{font-family:var(--wx-display);font-weight:400}
.wx-block--signatures{margin-top:44px;break-inside:avoid}
.wx-signature-line{display:block;margin:36px 0 0}
.wx-signature-stroke{display:block;height:1px;background:var(--wx-ink);max-width:340px}
.wx-signature-name{display:block;font:400 calc(13px*var(--wx-scale))/1.5 var(--wx-display);color:var(--wx-ink);margin-top:8px}
.wx-unknown{outline:1px dashed var(--wx-lilac);padding:2px 4px}

/* Galeria de fotos (plano de tratamento, P-PLA-11): a grade quebra entre figuras, nunca no meio de uma */
.wx-block--gallery{break-inside:auto;container-type:inline-size}
.wx-block--gallery .wx-block-title{break-after:avoid}
.wx-gallery{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));align-items:end;gap:16px 12px;margin:0 0 4px}
.wx-gallery--2{grid-template-columns:repeat(2,minmax(0,1fr))}
.wx-gallery-item{margin:0;min-width:0;break-inside:avoid;page-break-inside:avoid}
.wx-gallery-image{display:block;max-width:100%;max-height:280px;width:auto;height:auto;background:var(--wx-pampas)}
.wx-gallery-caption{margin-top:6px;font:300 calc(12px*var(--wx-scale))/1.5 var(--wx-body);color:var(--wx-secondary)}
.wx-gallery-caption-title{display:block;font:400 calc(13px*var(--wx-scale))/1.4 var(--wx-display);color:var(--wx-ink)}
.wx-gallery-caption-detail{display:block}
.wx-gallery-empty{margin:0;font-size:calc(13px*var(--wx-scale));color:var(--wx-secondary)}
@container (max-width:480px){.wx-gallery,.wx-gallery--2{grid-template-columns:minmax(0,1fr)}}

/* Anamnese (P-DOC-03): o bloco atravessa páginas; a pergunta nunca se separa da resposta */
.wx-block--anamnesis{break-inside:auto}
.wx-block--anamnesis .wx-block-title{break-after:avoid}
.wx-anamnesis{margin:0}
.wx-anamnesis-section{margin:0 0 20px;break-inside:auto}
.wx-anamnesis-section:last-of-type{margin-bottom:12px}
.wx-anamnesis-section-title{font:400 calc(14px*var(--wx-scale))/1.4 var(--wx-display);color:var(--wx-ink);padding:8px 0;border-bottom:1px solid var(--wx-hair-strong);break-after:avoid}
.wx-anamnesis-item{padding:10px 0;border-bottom:1px solid var(--wx-hair);break-inside:avoid;page-break-inside:avoid}
.wx-anamnesis-question{font:300 calc(13px*var(--wx-scale))/1.5 var(--wx-body);color:var(--wx-secondary)}
.wx-anamnesis-answer{font:400 calc(14px*var(--wx-scale))/1.5 var(--wx-display);color:var(--wx-ink);margin-top:2px;white-space:pre-line}
.wx-anamnesis-item[data-state="unanswered"] .wx-anamnesis-answer,.wx-anamnesis-item[data-state="skipped"] .wx-anamnesis-answer{font:300 calc(13px*var(--wx-scale))/1.5 var(--wx-body);color:var(--wx-secondary)}
.wx-anamnesis-note{font:300 calc(12px*var(--wx-scale))/1.6 var(--wx-body);color:var(--wx-secondary);margin-top:4px;white-space:pre-line}
.wx-anamnesis-meta{margin:0;font:300 11px/1.6 var(--wx-body);color:var(--wx-label);break-before:avoid}
.wx-anamnesis-empty{margin:0;font-size:calc(13px*var(--wx-scale));color:var(--wx-secondary)}

/* Ficha em branco (RN-ANA-24): o mesmo bloco para imprimir e preencher à mão. Linhas na altura da caneta (8mm),
   caixas de 4mm em preto, a pergunta nunca se separa das opções e das linhas */
.wx-anamnesis--blank{container-type:inline-size}
.wx-anamnesis--blank .wx-anamnesis-question{font:400 calc(14px*var(--wx-scale))/1.5 var(--wx-body);color:var(--wx-ink)}
.wx-anamnesis-number{font-family:var(--wx-display);font-variant-numeric:tabular-nums;margin-right:6px}
.wx-legal .wx-anamnesis-intro{margin:8px 0 0;font:300 calc(13px*var(--wx-scale))/1.6 var(--wx-body);color:var(--wx-secondary)}
.wx-anamnesis-lead{break-inside:avoid;page-break-inside:avoid}
.wx-anamnesis-context,.wx-anamnesis-condition,.wx-anamnesis-hint{font:300 calc(12px*var(--wx-scale))/1.6 var(--wx-body);color:var(--wx-secondary);margin-top:2px}
.wx-anamnesis-condition{font-style:italic}
.wx-anamnesis-identification .wx-anamnesis-section-title{break-after:avoid;page-break-after:avoid}
.wx-anamnesis-fields{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px 8mm;padding:12px 0 4px}
.wx-anamnesis-field{min-width:0;break-inside:avoid;page-break-inside:avoid}
.wx-anamnesis-field[data-span="wide"]{grid-column:1/-1}
.wx-anamnesis-field-label{font:300 11px/1.2 var(--wx-body);letter-spacing:.12em;text-transform:uppercase;color:var(--wx-label)}
.wx-anamnesis-field-value{display:flex;align-items:flex-end;min-height:8mm;padding-bottom:1mm;border-bottom:1px solid var(--wx-ink);font:400 calc(14px*var(--wx-scale))/1.4 var(--wx-display);color:var(--wx-ink)}
.wx-anamnesis-line{display:block;height:8mm;border-bottom:1px solid var(--wx-ink)}
.wx-anamnesis-line--short{max-width:48mm}
.wx-anamnesis-date{display:inline-flex;align-items:flex-end;gap:4px;height:8mm;margin-top:4px;font:300 calc(14px*var(--wx-scale))/1 var(--wx-body);color:var(--wx-secondary)}
.wx-anamnesis-date-part{display:inline-block;width:10mm;height:100%;border-bottom:1px solid var(--wx-ink)}
.wx-anamnesis-date-part--year{width:16mm}
.wx-anamnesis-field .wx-anamnesis-date{margin-top:0}
.wx-legal .wx-anamnesis-options{display:flex;flex-wrap:wrap;gap:6px 24px;margin:8px 0 0;padding:0;list-style:none}
.wx-legal .wx-anamnesis-options[data-layout="list"]{flex-direction:column;gap:6px}
.wx-legal .wx-anamnesis-options[data-layout="columns"]{display:block;columns:2;column-gap:8mm}
.wx-legal .wx-anamnesis-option{display:flex;align-items:flex-start;gap:8px;margin:0;font:300 calc(13px*var(--wx-scale))/1.5 var(--wx-body);color:var(--wx-ink);break-inside:avoid}
.wx-legal .wx-anamnesis-options[data-layout="columns"] .wx-anamnesis-option{margin:0 0 6px}
.wx-legal .wx-anamnesis-option--skip{margin-top:8px;color:var(--wx-secondary)}
.wx-anamnesis-box{flex:none;width:4mm;height:4mm;margin-top:.2em;border:1px solid var(--wx-ink);background:var(--wx-surface)}
.wx-anamnesis-option-examples{color:var(--wx-secondary)}
.wx-anamnesis-followup{margin-top:8px}
.wx-anamnesis-followup-label{font:300 calc(12px*var(--wx-scale))/1.5 var(--wx-body);color:var(--wx-secondary)}
@container (max-width:480px){.wx-anamnesis-fields{grid-template-columns:minmax(0,1fr)}.wx-legal .wx-anamnesis-options[data-layout="columns"]{columns:1}}

/* Termo definido e variáveis */
.wx-term{font-style:normal;border-bottom:1px dotted var(--wx-plum);cursor:help}
.wx-var{font-weight:400}
.wx-var--empty{color:var(--wx-plum);background:rgba(166,146,186,.18);border-bottom:1px dashed var(--wx-plum);padding:0 3px;font-family:ui-monospace,monospace;font-size:.85em}

/* Quebra de página e prova */
.wx-page-break{height:0;margin:0}
.wx-evidence{margin-top:40px;padding-top:16px;border-top:1px solid var(--wx-hair-strong);font:300 12px/1.8 var(--wx-body);color:var(--wx-secondary);break-inside:avoid}
.wx-evidence-title{font:300 11px/1 var(--wx-body);letter-spacing:.14em;text-transform:uppercase;color:var(--wx-label);margin-bottom:6px}
.wx-evidence b{font-family:var(--wx-display);font-weight:400;color:var(--wx-ink)}
.wx-evidence-hash{font-family:ui-monospace,monospace;font-size:11px;word-break:break-all}
.wx-brand-footer{margin-top:28px;font:300 11px/1.6 var(--wx-body);color:var(--wx-label)}

/* Página de evidências (P-ASS-06) */
.wx-evidence-page{margin-top:40px;padding-top:24px;border-top:1px solid var(--wx-hair-strong);font:300 12px/1.7 var(--wx-body);color:var(--wx-ink);break-before:page}
.wx-evidence-page-eyebrow{font:300 11px/1 var(--wx-body);letter-spacing:.14em;text-transform:uppercase;color:var(--wx-label)}
.wx-evidence-page-title{font:300 22px/1.2 var(--wx-display);color:var(--wx-ink);margin:10px 0 18px}
.wx-evidence-facts{padding-bottom:14px;border-bottom:1px solid var(--wx-hair)}
.wx-evidence-line{margin:0 0 4px}
.wx-evidence-label{font:300 10px/1 var(--wx-body);letter-spacing:.12em;text-transform:uppercase;color:var(--wx-label);margin-right:6px}
.wx-evidence-signer{padding:14px 0;border-bottom:1px solid var(--wx-hair);break-inside:avoid}
.wx-evidence-signer-role{font:400 13px/1.3 var(--wx-display);color:var(--wx-ink);margin-bottom:6px}
.wx-evidence-drawn{margin:8px 0 4px}
.wx-evidence-drawn img{display:block;max-height:64px;max-width:240px;border-bottom:1px solid var(--wx-hair-strong)}
.wx-evidence-page-foot{margin-top:16px;font:300 10px/1.6 var(--wx-body);color:var(--wx-label)}

/* Impressão */
@media print{
  .wx-legal{--wx-max:none;font-size:calc(11.5pt*var(--wx-scale));line-height:1.6;color:#000}
  .wx-legal a{color:#000;border-bottom:0}
  .wx-page-break{break-after:page}
  .wx-term{border-bottom:0}
  .wx-term::after{content:" (" attr(data-def) ")";font-style:italic;color:var(--wx-secondary)}
  .wx-var--empty{background:none;border-bottom:1px solid #000;font-family:inherit;font-size:inherit;color:#000}
  .wx-block--risks,.wx-block--attention,.wx-block--context{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .wx-gallery-image{max-height:70mm;background:none}
  .wx-anamnesis-item{break-inside:avoid;page-break-inside:avoid}
  .wx-anamnesis-section-title{break-after:avoid;page-break-after:avoid}
  .wx-anamnesis-box,.wx-anamnesis-line,.wx-anamnesis-field-value,.wx-anamnesis-date-part{border-color:#000}
  .wx-watermark{position:fixed;top:50%;-webkit-print-color-adjust:exact;print-color-adjust:exact}
}
@page{
  size:A4;margin:22mm 18mm 24mm;
  @bottom-left{content:${left};font:300 9pt/1 Lato,sans-serif;color:#5b5560}
  @bottom-right{content:${right} counter(page) " de " counter(pages);font:300 9pt/1 Lato,sans-serif;color:#5b5560}
}
`
}

/** `<link>` das fontes para o HTML autocontido. Troque por @font-face próprio via `fontsCss`. */
export const GOOGLE_FONTS_LINK =
  '<link rel="preconnect" href="https://fonts.googleapis.com"><link href="https://fonts.googleapis.com/css2?family=Lexend:wght@300;400&family=Lato:wght@300;400;700&display=swap" rel="stylesheet">'
